import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api';
import { hinweis } from '../components/Hinweise';

const TYP_OPTIONS = [
  { value: 'anmeldung_bestaetigung', label: 'Anmeldungsbestätigung' },
  { value: 'warteliste',             label: 'Warteliste' },
  { value: 'zahlung_bestaetigung',   label: 'Zahlungsbestätigung' },
  { value: 'warteliste_platz_angeboten', label: 'Warteliste: Platzangebot' },
  { value: 'warteliste_abgelehnt',       label: 'Warteliste: Abgelehnt' },
  { value: 'custom',                 label: 'Eigene Vorlage' },
];

const INIT = { name: '', betreff: '', typ: 'custom', ist_allgemein: false, html_inhalt: '', text_inhalt: '', aktiv: true };

export default function EmailVorlagenForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = id && id !== 'neu';

  const [form, setForm] = useState(INIT);
  const [platzhalter, setPlatzhalter] = useState([]);
  const [vorschau, setVorschau] = useState(null);
  const [showVorschau, setShowVorschau] = useState(searchParams.get('vorschau') === '1');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('html'); // 'html' | 'text'

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  useEffect(() => {
    api.get('/api/admin/email-vorlagen/platzhalter').then(r => setPlatzhalter(r.data)).catch(() => {});

    if (isEdit) {
      api.get(`/api/admin/email-vorlagen/${id}`).then(r => {
        const v = r.data;
        setForm({ name: v.name, betreff: v.betreff, typ: v.typ, ist_allgemein: v.ist_allgemein,
          html_inhalt: v.html_inhalt, text_inhalt: v.text_inhalt || '', aktiv: v.aktiv });
        setLoading(false);
        if (searchParams.get('vorschau') === '1') loadVorschau();
      }).catch(() => { setError('Vorlage konnte nicht geladen werden'); setLoading(false); });
    }
  }, [id, isEdit]);

  const loadVorschau = async () => {
    if (!isEdit) return;
    try {
      const r = await api.post(`/api/admin/email-vorlagen/${id}/vorschau`);
      setVorschau(r.data);
      setShowVorschau(true);
    } catch (err) { hinweis.fehler(err.response?.data?.error || 'Fehler bei Vorschau'); }
  };

  const insertPlatzhalter = (key) => {
    if (activeTab === 'html') set('html_inhalt', form.html_inhalt + key);
    else set('text_inhalt', form.text_inhalt + key);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return setError('Name ist Pflicht');
    if (!form.betreff.trim()) return setError('Betreff ist Pflicht');
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await api.put(`/api/admin/email-vorlagen/${id}`, form);
      } else {
        await api.post('/api/admin/email-vorlagen', form);
      }
      navigate('/email-vorlagen');
    } catch (err) { setError(err.response?.data?.error || 'Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div className="page-header">
        <h2 className="page-title">{isEdit ? 'E-Mail-Vorlage bearbeiten' : 'Neue E-Mail-Vorlage'}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isEdit && <button className="btn btn-secondary btn-sm" onClick={loadVorschau}>Vorschau</button>}
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/email-vorlagen')}>Abbrechen</button>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: '1.25rem', alignItems: 'start' }}>
        {/* Hauptbereich */}
        <div>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Name (intern)</label>
                <input className="form-control" value={form.name} maxLength={200}
                  onChange={e => set('name', e.target.value)} placeholder="z.B. Bestätigung Schwimmkurs" />
              </div>
              <div className="form-group">
                <label className="form-label">Typ</label>
                <select className="form-control" value={form.typ} onChange={e => set('typ', e.target.value)}>
                  {TYP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Betreff</label>
              <input className="form-control" value={form.betreff} maxLength={300}
                onChange={e => set('betreff', e.target.value)}
                placeholder="z.B. Deine Anmeldung: {{angebot_titel}}" />
            </div>
            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.25rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem' }}>
                <input type="checkbox" checked={form.ist_allgemein}
                  onChange={e => set('ist_allgemein', e.target.checked)} style={{ width: 15, height: 15 }} />
                Allgemeine Vorlage (gilt für alle Angebote)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem' }}>
                <input type="checkbox" checked={form.aktiv}
                  onChange={e => set('aktiv', e.target.checked)} style={{ width: 15, height: 15 }} />
                Aktiv
              </label>
            </div>
          </div>

          <div className="card">
            <div style={{ display: 'flex', gap: 0, marginBottom: '0.75rem', borderBottom: '1px solid var(--border)' }}>
              {['html', 'text'].map(t => (
                <button key={t} type="button" onClick={() => setActiveTab(t)}
                  style={{
                    padding: '0.4rem 1rem', border: 'none', background: 'transparent', cursor: 'pointer',
                    fontWeight: activeTab === t ? 700 : 400,
                    color: activeTab === t ? 'var(--primary)' : 'var(--text-muted)',
                    borderBottom: activeTab === t ? '2px solid var(--primary)' : '2px solid transparent',
                    marginBottom: -1, textTransform: 'uppercase', fontSize: '0.82rem', letterSpacing: '0.05em',
                  }}>
                  {t === 'html' ? 'HTML-Inhalt' : 'Text-Version'}
                </button>
              ))}
            </div>

            {activeTab === 'html' && (
              <textarea className="form-control" rows={20}
                value={form.html_inhalt} onChange={e => set('html_inhalt', e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}
                placeholder="HTML-Inhalt der E-Mail. Verwende {{platzhalter}} für dynamische Inhalte." />
            )}
            {activeTab === 'text' && (
              <textarea className="form-control" rows={20}
                value={form.text_inhalt} onChange={e => set('text_inhalt', e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}
                placeholder="Nur-Text-Version der E-Mail (optional, für E-Mail-Clients ohne HTML-Unterstützung)." />
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Speichern…' : '✓ Speichern'}
            </button>
          </div>
        </div>

        {/* Sidebar: Platzhalter */}
        <div>
          <div className="card" style={{ position: 'sticky', top: '1rem' }}>
            <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Platzhalter
            </p>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              Klicken zum Einfügen in {activeTab === 'html' ? 'HTML' : 'Text'}:
            </p>
            {platzhalter.map(p => (
              <div key={p.key} style={{ marginBottom: '0.5rem' }}>
                <button type="button" onClick={() => insertPlatzhalter(p.key)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '4px 8px',
                    background: '#f0f4f8', border: '1px solid var(--border)', borderRadius: 4,
                    cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--primary)',
                    fontWeight: 600, marginBottom: 2,
                  }}>
                  {p.key}
                </button>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', paddingLeft: 4 }}>{p.beschreibung}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Vorschau Modal */}
      {showVorschau && vorschau && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }}>
          <div style={{ background: '#fff', borderRadius: 10, width: '100%', maxWidth: 700, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>Vorschau (Testdaten)</strong>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowVorschau(false)}>Schließen</button>
            </div>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Betreff: </span>
              <span>{vorschau.betreff}</span>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <iframe srcDoc={vorschau.html} style={{ width: '100%', height: 400, border: 'none' }} title="Vorschau" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
